import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const JoinWorkspacePage = () => {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuthAndFetchInvite();
  }, [inviteToken]);

  const checkAuthAndFetchInvite = async () => {
    try {
      // Check if user is logged in
      const authResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(authResponse.data);
    } catch (err) {
      // Not logged in - will redirect to login
      setUser(null);
    }

    try {
      // Fetch invite info (public endpoint)
      const inviteResponse = await axios.get(`${API}/workspaces/invite/${inviteToken}`);
      setInvite(inviteResponse.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user) {
      // Store invite token and redirect to login
      sessionStorage.setItem('pendingInvite', inviteToken);
      window.location.href = `${API}/auth/google`;
      return;
    }

    setJoining(true);
    try {
      await axios.post(`${API}/workspaces/join/${inviteToken}`, {}, { withCredentials: true });
      toast.success(`You've joined ${invite.workspace_name}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join workspace');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Invalid Invite</h1>
          <p className="text-zinc-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">You&apos;re Invited!</h1>
          <p className="text-zinc-600">
            Join <span className="font-semibold text-indigo-600">{invite?.workspace_name}</span> workspace
          </p>
        </div>

        <div className="bg-zinc-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-zinc-600">Role</span>
            <span className="font-semibold text-zinc-900 capitalize">
              {invite?.role === 'design_reviewer' ? 'Design Reviewer' : 
               invite?.role === 'qa' ? 'QA Tester' : invite?.role}
            </span>
          </div>
          <hr className="my-3 border-zinc-200" />
          <div className="text-xs text-zinc-500">
            {invite?.role === 'editor' && 'You will be able to create and edit designs'}
            {invite?.role === 'viewer' && 'You will be able to view designs'}
            {invite?.role === 'design_reviewer' && 'You will be able to review assigned projects'}
            {invite?.role === 'qa' && 'You will be able to test as customers and manage credits'}
          </div>
        </div>

        {user ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <Check className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm font-medium text-green-800">Signed in as</p>
                <p className="text-xs text-green-600">{user.email}</p>
              </div>
            </div>
            
            <Button onClick={handleJoin} disabled={joining} className="w-full bg-indigo-600 hover:bg-indigo-700">
              {joining ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Joining...
                </>
              ) : (
                'Join Workspace'
              )}
            </Button>
          </div>
        ) : (
          <Button onClick={handleJoin} className="w-full bg-indigo-600 hover:bg-indigo-700">
            Sign in with Google to Join
          </Button>
        )}

        <p className="text-center text-xs text-zinc-400 mt-6">
          By joining, you&apos;ll share credits with the workspace owner.
        </p>
      </div>
    </div>
  );
};

export default JoinWorkspacePage;
